<script setup>
import { isVNode, computed } from 'vue';
import Icon from 'next/icon/Icon.vue';
import Policy from 'dashboard/components/policy.vue';
import { useSidebarContext } from './provider';
import SidebarUnreadBadge from './SidebarUnreadBadge.vue';

const props = defineProps({
  label: { type: String, required: true },
  to: { type: [String, Object], required: true },
  icon: { type: [String, Object], default: null },
  active: { type: Boolean, default: false },
  component: { type: Function, default: null },
  badgeCount: { type: [Number, String], default: 0 },
  badgeTooltip: { type: String, default: '' },
  badgeShowZero: { type: Boolean, default: false },
  hideTreeLine: { type: Boolean, default: false },
  thinTreeLine: { type: Boolean, default: false },
});

const { resolvePermissions, resolveFeatureFlag } = useSidebarContext();

const shouldRenderComponent = computed(() => {
  return typeof props.component === 'function' || isVNode(props.component);
});

// Tree-line connector per leaf: vertical line (::before) + rounded elbow on the
// last child (::after). Logical props (start / border-s / rounded-es)
const TREE_CONNECTOR =
  "child-item before:content-[''] before:absolute before:start-0 before:w-0.5 before:h-full before:bg-n-slate-4 first:before:rounded-t last:before:h-1/5 last:after:content-[''] last:after:absolute last:after:start-0 last:after:bottom-[calc(50%_-_2px)] last:after:h-3 last:after:w-2.5 last:after:border-b-2 last:after:border-s-2 last:after:rounded-es last:after:border-n-slate-4";
</script>

<template>
  <Policy
    :permissions="resolvePermissions(to)"
    :feature-flag="resolveFeatureFlag(to)"
    as="li"
    class="py-0.5 ps-2 ms-3 relative text-n-slate-11 min-w-0"
    :class="{
      [TREE_CONNECTOR]: !hideTreeLine,
      'before:!w-px last:after:!border-b last:after:!border-s':
        !hideTreeLine && thinTreeLine,
    }"
  >
    <component
      :is="to ? 'router-link' : 'div'"
      :to="to"
      :title="label"
      class="group relative flex h-9 min-w-0 items-center gap-2 rounded-xl px-2 py-1 transition-all hover:bg-n-alpha-2"
      :class="{
        'active bg-n-brand/10 text-n-brand ring-1 ring-n-brand/15 dark:bg-n-brand/15':
          active,
      }"
    >
      <span
        v-if="active"
        class="absolute inset-y-2 start-0 w-0.5 rounded-full bg-n-brand"
      />
      <component
        :is="component"
        v-if="shouldRenderComponent"
        v-bind="{
          label,
          icon,
          active,
          badgeCount,
          badgeTooltip,
          badgeShowZero,
        }"
      />
      <template v-else>
        <span
          v-if="icon"
          class="grid size-6 shrink-0 place-content-center rounded-lg transition-colors"
          :class="active ? 'bg-n-brand/15' : 'bg-n-alpha-1'"
        >
          <Icon :icon="icon" class="inline-block size-3.5" />
        </span>
        <div class="flex-1 truncate min-w-0 text-sm">{{ label }}</div>
        <SidebarUnreadBadge
          :count="badgeCount"
          :tooltip="badgeTooltip"
          :show-zero="badgeShowZero"
        />
      </template>
    </component>
  </Policy>
</template>
